import { services } from '@config/services';
import type {
  AccessRequest,
  AccessRequestCounts,
  AccessRequestsListResponse,
} from '../types';
import { integrationApi } from './_base';

/**
 * Access-request endpoints (`/v1/accessRequest*`) — the merchant's
 * Request Access click, the owner's approval queue, and the homepage counts.
 *
 * Every route here is dark until `om_tech_21418_request_access` is on:
 * integration-service returns 404 while the flag is off, so callers must sit
 * behind the flag gate (`../AccessRequests/flag.ts`) rather than reading an
 * empty list as "nothing to approve".
 *
 * Two scoping rules the portal must never work around, because the service
 * derives both from the caller and ignores anything else:
 *   - the requesting merchant is always the caller (or the merchant an admin
 *     is viewing as) — there is no merchantId to send;
 *   - `view` picks between the caller's own two scopes, and cannot widen
 *     access: `incoming` is every request against plugins the caller owns,
 *     `outbound` is the caller's own requests.
 */

/** Query parameters for `GET /v1/accessRequest`. */
export interface AccessRequestListArgs {
  /** `incoming` = the caller's approval queue; `outbound` = their own requests. */
  view?: 'incoming' | 'outbound';
  /** Pending | Approved | Rejected | Cancelled | Revoked. Omit for all. */
  status?: string;
  /** Case-insensitive merchant-name contains. Only meaningful on `incoming`. */
  search?: string;
  /**
   * Narrows to one plugin. Honoured on BOTH views — the connection page uses
   * it with `view=outbound` to find this merchant's latest request for the
   * plugin it is rendering, which is what picks the button state.
   */
  pluginId?: string;
  /** Honoured on `incoming` only; the service ignores it on `outbound`. */
  merchantId?: string;
  page?: number;
  pageSize?: number;
  /** See `AccessScoped.accessId`. */
  accessId?: string;
}

/** Body for the reject route; the reason is optional and merchant-visible. */
export interface RejectAccessRequestArgs extends AccessScoped {
  requestId: string;
  reason?: string;
}

/** The reason is merchant-visible and the only explanation they get; nothing notifies them. */
export interface RevokeAccessRequestArgs extends AccessScoped {
  requestId: string;
  reason?: string;
}

/**
 * Includes the connections list because its `accessStatus` is derived from the latest request.
 * Without a `pluginId` the whole type is invalidated.
 */
export function accessDecisionTags(
  pluginId: string | undefined,
): Array<{ type: 'AccessRequests' | 'AccessRequestCounts' | 'PluginConnections'; id?: string } | 'PluginConnections'> {
  return [
    { type: 'AccessRequests', id: 'LIST' },
    { type: 'AccessRequestCounts', id: 'ME' },
    pluginId ? { type: 'PluginConnections', id: pluginId } : 'PluginConnections',
  ];
}

export interface ClosePluginAccessRequestsArgs extends AccessScoped {
  pluginId: string;
  /** The cancel route takes no reason. */
  reason?: string;
}

/**
 * Carries the caller's organization context to a route that accepts no
 * merchant or owner id.
 */
export interface AccessScoped {
  /**
   * `X-Access-IDs` for this call (`m-{id}` / `v-{id}`), from
   * `useAccessScopeId()`, or undefined outside an organization context.
   *
   * An admin has no organization of their own, so `AccessRequestScope` in
   * integration-service resolves them through this header alone — without it
   * their queue is empty and a submit is refused. It is set per request because
   * `baseQuery` sends no `X-Access-IDs`; these are the last calls that need it.
   *
   * It is part of the cache key on the read endpoints, which is what keeps two
   * View As contexts from sharing one cached queue.
   */
  accessId?: string;
}

/** Applies the scope header to a built request, when there is one to apply. */
function scoped<T extends object>(built: T, accessId?: string): T {
  return accessId ? { ...built, headers: { 'X-Access-IDs': accessId } } : built;
}

/**
 * Request descriptors exported separately so the wire-shape lock tests can
 * assert URL/method/body without going through the network layer (mirrors
 * `templateRequestBuilders` in ./templates.ts).
 */
export const accessRequestBuilders = {
  submitAccessRequest: (
    pluginId: string,
  ): { url: string; method: 'POST'; body: { pluginId: string } } => ({
    url: `${services.integration}/v1/accessRequest`,
    method: 'POST',
    body: { pluginId },
  }),
  getAccessRequests: (args: AccessRequestListArgs | void): { url: string; method: 'GET' } => {
    const p = new URLSearchParams();
    if (args?.view) p.set('view', args.view);
    if (args?.status) p.set('status', args.status);
    if (args?.search) p.set('search', args.search);
    if (args?.pluginId) p.set('pluginId', args.pluginId);
    if (args?.merchantId) p.set('merchantId', args.merchantId);
    if (args?.page) p.set('page', String(args.page));
    if (args?.pageSize) p.set('pageSize', String(args.pageSize));
    const qs = p.toString();
    return {
      url: `${services.integration}/v1/accessRequest${qs ? `?${qs}` : ''}`,
      method: 'GET',
    };
  },
  getAccessRequestCounts: (): { url: string; method: 'GET' } => ({
    url: `${services.integration}/v1/accessRequest/count`,
    method: 'GET',
  }),
  approveAccessRequest: (requestId: string): { url: string; method: 'PATCH' } => ({
    url: `${services.integration}/v1/accessRequest/${encodeURIComponent(requestId)}/approve`,
    method: 'PATCH',
  }),
  rejectAccessRequest: ({
    requestId,
    reason,
  }: RejectAccessRequestArgs): { url: string; method: 'PATCH'; body: { reason?: string } } => ({
    url: `${services.integration}/v1/accessRequest/${encodeURIComponent(requestId)}/reject`,
    method: 'PATCH',
    // The service accepts an absent body. Send `{}` rather than nothing when
    // there is no reason, so the request always carries a JSON content type.
    body: reason && reason.trim() ? { reason: reason.trim() } : {},
  }),
  cancelAccessRequest: (requestId: string): { url: string; method: 'PATCH' } => ({
    url: `${services.integration}/v1/accessRequest/${encodeURIComponent(requestId)}/cancel`,
    method: 'PATCH',
  }),
  revokeAccessRequest: ({
    requestId,
    reason,
  }: RevokeAccessRequestArgs): { url: string; method: 'PATCH'; body: { reason?: string } } => ({
    url: `${services.integration}/v1/accessRequest/${encodeURIComponent(requestId)}/revoke`,
    method: 'PATCH',
    body: reason && reason.trim() ? { reason: reason.trim() } : {},
  }),
  cancelPendingForPlugin: (pluginId: string): { url: string; method: 'PATCH' } => ({
    url: `${services.integration}/v1/accessRequest/plugin/${encodeURIComponent(pluginId)}/cancelPending`,
    method: 'PATCH',
  }),
  revokeApprovedForPlugin: ({
    pluginId,
    reason,
  }: ClosePluginAccessRequestsArgs): {
    url: string;
    method: 'PATCH';
    body: { reason?: string };
  } => ({
    url: `${services.integration}/v1/accessRequest/plugin/${encodeURIComponent(pluginId)}/revokeApproved`,
    method: 'PATCH',
    body: reason && reason.trim() ? { reason: reason.trim() } : {},
  }),
};

/** Unwraps the list envelope, tolerating a bare array. */
function unwrapList(
  response: AccessRequestsListResponse | AccessRequest[],
): AccessRequestsListResponse {
  if (Array.isArray(response)) {
    return { accessRequests: response, total: response.length };
  }
  return {
    accessRequests: response.accessRequests ?? [],
    total: response.total ?? response.accessRequests?.length ?? 0,
  };
}

/** Unwraps the single-request envelope the write routes return. */
function unwrapOne(
  response: { accessRequest?: AccessRequest } | AccessRequest,
): AccessRequest | undefined {
  const wrapped = response as { accessRequest?: AccessRequest };
  return wrapped.accessRequest ?? (response as AccessRequest);
}

const accessRequestsApi = integrationApi.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * `POST /v1/accessRequest` — the merchant's Request Access click, and the
     * only way a request is created. Idempotent: a second click returns the
     * request already outstanding rather than erroring, so the button does not
     * have to guard against a double submit to stay correct.
     */
    submitAccessRequest: builder.mutation<
      AccessRequest | undefined,
      { pluginId: string } & AccessScoped
    >({
      query: ({ pluginId, accessId }) =>
        scoped(accessRequestBuilders.submitAccessRequest(pluginId), accessId),
      transformResponse: unwrapOne,
      invalidatesTags: (result) => accessDecisionTags(result?.pluginId),
    }),
    getAccessRequests: builder.query<AccessRequestsListResponse, AccessRequestListArgs | void>({
      query: (args) =>
        scoped(accessRequestBuilders.getAccessRequests(args), args?.accessId),
      transformResponse: unwrapList,
      providesTags: () => [{ type: 'AccessRequests', id: 'LIST' }],
    }),
    /**
     * `GET /v1/accessRequest/count` → `{ incoming, outbound }`. Highest-
     * frequency call in the epic (both homepage cards on every load), and
     * cached 60s server-side, so it is deliberately not polled here.
     */
    getAccessRequestCounts: builder.query<AccessRequestCounts, AccessScoped | void>({
      query: (args) =>
        scoped(accessRequestBuilders.getAccessRequestCounts(), args?.accessId),
      transformResponse: (
        response: AccessRequestCounts | { counts?: AccessRequestCounts },
      ): AccessRequestCounts => {
        const wrapped = response as { counts?: AccessRequestCounts };
        const counts = wrapped.counts ?? (response as AccessRequestCounts);
        return { incoming: counts?.incoming ?? 0, outbound: counts?.outbound ?? 0 };
      },
      providesTags: () => [{ type: 'AccessRequestCounts', id: 'ME' }],
    }),
    /**
     * Approve grants permission and nothing else — it does not enable the
     * merchant's connection. The merchant enables it themselves afterwards,
     * which is what performs the vendor ASN subscription while they are there
     * to see it fail.
     */
    approveAccessRequest: builder.mutation<
      AccessRequest | undefined,
      { requestId: string } & AccessScoped
    >({
      query: ({ requestId, accessId }) =>
        scoped(accessRequestBuilders.approveAccessRequest(requestId), accessId),
      transformResponse: unwrapOne,
      invalidatesTags: (result) => accessDecisionTags(result?.pluginId),
    }),
    rejectAccessRequest: builder.mutation<AccessRequest | undefined, RejectAccessRequestArgs>({
      query: (args) => scoped(accessRequestBuilders.rejectAccessRequest(args), args.accessId),
      transformResponse: unwrapOne,
      invalidatesTags: (result) => accessDecisionTags(result?.pluginId),
    }),
    /** Not a rejection: winding a plugin down is no reason about the merchant, so it takes none. */
    cancelAccessRequest: builder.mutation<
      AccessRequest | undefined,
      { requestId: string } & AccessScoped
    >({
      query: ({ requestId, accessId }) =>
        scoped(accessRequestBuilders.cancelAccessRequest(requestId), accessId),
      transformResponse: unwrapOne,
      invalidatesTags: (result) => accessDecisionTags(result?.pluginId),
    }),
    /** Does not disconnect: the revoke time is an effective-from date, so in-flight work finishes. */
    revokeAccessRequest: builder.mutation<AccessRequest | undefined, RevokeAccessRequestArgs>({
      query: (args) => scoped(accessRequestBuilders.revokeAccessRequest(args), args.accessId),
      transformResponse: unwrapOne,
      invalidatesTags: (result) => accessDecisionTags(result?.pluginId),
    }),
    cancelPendingForPlugin: builder.mutation<{ closed: number }, ClosePluginAccessRequestsArgs>({
      query: ({ pluginId, accessId }) =>
        scoped(accessRequestBuilders.cancelPendingForPlugin(pluginId), accessId),
      transformResponse: (response: { closed?: number }) => ({ closed: response?.closed ?? 0 }),
      invalidatesTags: (_r, _e, { pluginId }) => accessDecisionTags(pluginId),
    }),
    revokeApprovedForPlugin: builder.mutation<{ closed: number }, ClosePluginAccessRequestsArgs>({
      query: (args) =>
        scoped(accessRequestBuilders.revokeApprovedForPlugin(args), args.accessId),
      transformResponse: (response: { closed?: number }) => ({ closed: response?.closed ?? 0 }),
      invalidatesTags: (_r, _e, { pluginId }) => accessDecisionTags(pluginId),
    }),
  }),
});

export const {
  useSubmitAccessRequestMutation,
  useGetAccessRequestsQuery,
  useGetAccessRequestCountsQuery,
  useApproveAccessRequestMutation,
  useRejectAccessRequestMutation,
  useCancelAccessRequestMutation,
  useRevokeAccessRequestMutation,
  useCancelPendingForPluginMutation,
  useRevokeApprovedForPluginMutation,
} = accessRequestsApi;
